import { useState } from "react";
import { Eye, EyeOff, Monitor, Smartphone, X } from "lucide-react";
import { Button } from "@learn-me/ui";
import { InteractiveContent, AudioPlayer } from "@/components/content";

interface ContentPreviewProps {
  content: string;
  audioUrl?: string;
  audioDuration?: number;
  title?: string;
  /** If true, renders as modal directly without button */
  isOpen?: boolean;
  /** Callback when modal should close */
  onClose?: () => void;
}

export function ContentPreview({
  content,
  audioUrl,
  audioDuration,
  title,
  isOpen,
  onClose,
}: ContentPreviewProps) {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [deviceMode, setDeviceMode] = useState<"desktop" | "mobile">("desktop");

  // External control mode: rendered as modal when isOpen is true
  const isExternallyControlled = isOpen !== undefined;
  const showModal = isExternallyControlled ? isOpen : isPreviewMode;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsPreviewMode(false);
    }
  };

  // Only show the trigger button in self-controlled mode
  if (!isExternallyControlled && !isPreviewMode) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsPreviewMode(true)}
        className="flex items-center gap-2"
      >
        <Eye className="h-4 w-4" />
        Vista Previa
      </Button>
    );
  }

  if (!showModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col max-h-[90vh] transition-all duration-300 ${
          deviceMode === "mobile" ? "w-[375px]" : "w-full max-w-4xl"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-xl">
          <h3 className="font-semibold text-gray-900">
            Vista Previa del Estudiante
          </h3>
          <div className="flex items-center gap-2">
            {/* Device toggles */}
            <div className="flex items-center bg-gray-200 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setDeviceMode("desktop")}
                className={`p-2 rounded-md transition-colors ${
                  deviceMode === "desktop"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Vista escritorio"
              >
                <Monitor className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setDeviceMode("mobile")}
                className={`p-2 rounded-md transition-colors ${
                  deviceMode === "mobile"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Vista movil"
              >
                <Smartphone className="h-4 w-4" />
              </button>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={handleClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Title */}
          {title && (
            <h1 className="text-2xl font-bold text-gray-900 mb-6">{title}</h1>
          )}

          {/* Audio Player */}
          {audioUrl && (
            <div className="mb-6">
              <AudioPlayer
                audioUrl={audioUrl}
                audioDuration={audioDuration}
                title={title}
              />
            </div>
          )}

          {/* Content */}
          {content ? (
            <InteractiveContent html={content} defaultExpanded={true} />
          ) : (
            <div className="text-center py-12 text-gray-500">
              <EyeOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay contenido para previsualizar</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-xl">
          <p className="text-xs text-gray-500 text-center">
            Esta es una vista previa de como vera el estudiante el contenido
          </p>
        </div>
      </div>
    </div>
  );
}
